import { useEffect, useState } from "react";
import { Check, Loader2, Pencil, Plus, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { type ReservationRow, handleControls, isSeededReservation } from "@/lib/handleControls";
import { RESERVATION_REASON_OPTIONS, formatDate } from "./shared";

type ReservationDraft = {
  original_username?: string;
  username: string;
  reason: string;
  reserved_until: string;
  allocation_kind: "none" | "user" | "workspace";
  allocated_to_user_id: string;
  allocated_to_workspace_id: string;
  note: string;
};

function emptyReservationDraft(): ReservationDraft {
  return {
    username: "", reason: "custom", reserved_until: "",
    allocation_kind: "none", allocated_to_user_id: "", allocated_to_workspace_id: "", note: "",
  };
}

export function ReservationsPanel() {
  const [rows, setRows] = useState<ReservationRow[]>([]);
  const [userLabels, setUserLabels] = useState<Record<string, { email: string | null; display_name: string | null }>>({});
  const [wsLabels, setWsLabels] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState<string>("__all");
  const [allocatedOnly, setAllocatedOnly] = useState(false);
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<ReservationDraft>(emptyReservationDraft());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await handleControls.reservation.list({
        search: search || undefined,
        reason: reason === "__all" ? null : reason,
        allocated_only: allocatedOnly || undefined,
        expiring_within_days: expiringSoon ? 30 : null,
      });
      setRows(data.rows); setUserLabels(data.user_labels); setWsLabels(data.workspace_labels);
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [reason, allocatedOnly, expiringSoon]);

  const openCreate = () => { setDraft(emptyReservationDraft()); setDialogOpen(true); };
  const openEdit = (r: ReservationRow) => {
    setDraft({
      original_username: r.username,
      username: r.username,
      reason: r.reason,
      reserved_until: r.reserved_until ? r.reserved_until.slice(0, 10) : "",
      allocation_kind: r.allocated_to_user_id ? "user" : r.allocated_to_workspace_id ? "workspace" : "none",
      allocated_to_user_id: r.allocated_to_user_id ?? "",
      allocated_to_workspace_id: r.allocated_to_workspace_id ?? "",
      note: r.note ?? "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        username: draft.username.trim().toLowerCase(),
        reason: draft.reason,
        reserved_until: draft.reserved_until ? new Date(draft.reserved_until).toISOString() : null,
        allocated_to_user_id: draft.allocation_kind === "user" ? draft.allocated_to_user_id.trim() || null : null,
        allocated_to_workspace_id: draft.allocation_kind === "workspace" ? draft.allocated_to_workspace_id.trim() || null : null,
        note: draft.note.trim() || null,
      };
      if (draft.original_username) {
        await handleControls.reservation.update({ ...payload, original_username: draft.original_username });
      } else {
        await handleControls.reservation.create(payload);
      }
      toast.success("Saved");
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  };

  const release = async (r: ReservationRow) => {
    if (!confirm(`Release reservation for @${r.username}?`)) return;
    try { await handleControls.reservation.release(r.username); toast.success("Released"); await load(); }
    catch (e) { toast.error((e as Error).message); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2"><Shield className="h-4 w-4" /> Handle reservations</CardTitle>
          <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4" /> Reserve a handle</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search handle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="w-56"
          />
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Reason" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All reasons</SelectItem>
              {RESERVATION_REASON_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={allocatedOnly} onChange={(e) => setAllocatedOnly(e.target.checked)} />
            Allocated only
          </label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={expiringSoon} onChange={(e) => setExpiringSoon(e.target.checked)} />
            Expiring ≤30 days
          </label>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Handle</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Allocated to</th>
                <th className="px-3 py-2">Note</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No reservations match.</td></tr>
              )}
              {rows.map((r) => {
                const seeded = isSeededReservation(r);
                const allocLabel = r.allocated_to_user_id
                  ? userLabels[r.allocated_to_user_id]?.email ?? r.allocated_to_user_id.slice(0, 8)
                  : r.allocated_to_workspace_id
                    ? wsLabels[r.allocated_to_workspace_id] ?? r.allocated_to_workspace_id.slice(0, 8)
                    : "—";
                return (
                  <tr key={r.username} className="border-t">
                    <td className="px-3 py-2 font-mono">@{r.username}</td>
                    <td className="px-3 py-2"><Badge variant="outline">{r.reason}</Badge></td>
                    <td className="px-3 py-2 text-muted-foreground">{formatDate(r.reserved_until)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.allocated_to_user_id ? <Badge className="mr-1" variant="secondary">user</Badge> : null}
                      {r.allocated_to_workspace_id ? <Badge className="mr-1" variant="secondary">workspace</Badge> : null}
                      {allocLabel}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[280px] truncate">{r.note ?? "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button variant="ghost" size="icon" disabled={seeded} onClick={() => openEdit(r)} aria-label="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {seeded && <TooltipContent>Seeded reservation — change in migration</TooltipContent>}
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button variant="ghost" size="icon" disabled={seeded} onClick={() => release(r)} aria-label="Release">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {seeded && <TooltipContent>Seeded reservation — change in migration</TooltipContent>}
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft.original_username ? `Edit @${draft.original_username}` : "Reserve a handle"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Handle</Label>
              <Input value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} placeholder="acmebrand" />
              <p className="mt-1 text-xs text-muted-foreground">3–24 chars, lowercase a–z and 0–9, must include a letter.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Reason</Label>
                <Select value={draft.reason} onValueChange={(v) => setDraft({ ...draft, reason: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESERVATION_REASON_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expires (optional)</Label>
                <Input type="date" value={draft.reserved_until} onChange={(e) => setDraft({ ...draft, reserved_until: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Allocate to</Label>
              <Select value={draft.allocation_kind} onValueChange={(v) => setDraft({ ...draft, allocation_kind: v as ReservationDraft["allocation_kind"] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No one (block-only)</SelectItem>
                  <SelectItem value="user">A specific user (auth user id)</SelectItem>
                  <SelectItem value="workspace">A specific workspace (profile id)</SelectItem>
                </SelectContent>
              </Select>
              {draft.allocation_kind === "user" && (
                <Input className="mt-2" placeholder="auth user UUID"
                  value={draft.allocated_to_user_id}
                  onChange={(e) => setDraft({ ...draft, allocated_to_user_id: e.target.value })} />
              )}
              {draft.allocation_kind === "workspace" && (
                <Input className="mt-2" placeholder="profile (workspace/persona) UUID"
                  value={draft.allocated_to_workspace_id}
                  onChange={(e) => setDraft({ ...draft, allocated_to_workspace_id: e.target.value })} />
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                When allocated, only that user/workspace can claim this handle. Allocation also overrides any blocked-substring match.
              </p>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Trademark watch, holding for X launch…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
